import type { ProcessedCourse } from "@/common/course/types";

/**
 * Monarch sends explicit section ordering for OC redesign templates. Sections not
 * listed in `mainContent` / `collapsableContent` are filtered out entirely.
 * Publisher-authored learner testimonials must still surface when populated on the
 * course record (RV2U-529).
 */

/** Earlier entries win; curriculum before emphasis so [emphasis, curriculum] inserts after curriculum. */
const INSERT_AFTER_PRIORITY = [
  "courseAbout",
  "curriculum",
  "emphasisModuleVersion",
  "midPageCta",
  "fullProgramCta",
] as const;

function courseHasLearnerTestimonials(
  course: Pick<ProcessedCourse, "learnerTestimonials"> | undefined,
): boolean {
  const t = course?.learnerTestimonials;
  if (t == null) return false;
  return typeof t === "string" && t.trim().length > 0;
}

export function augmentSectionOrderWithLearnerTestimonials<
  T extends { component: string },
>(
  sections: T[] | undefined,
  course: Pick<ProcessedCourse, "learnerTestimonials"> | undefined,
): T[] | undefined {
  if (!courseHasLearnerTestimonials(course)) {
    return sections;
  }

  // Undefined → caller renders full component stack; no merge needed (RV2U-529).
  if (sections == null || sections.length === 0) {
    return sections;
  }

  if (sections.some((s) => s.component === "learnerTestimonials")) {
    return sections;
  }

  let insertAt = sections.length;
  for (const key of INSERT_AFTER_PRIORITY) {
    const idx = sections.findIndex((s) => s.component === key);
    if (idx >= 0) {
      insertAt = idx + 1;
      break;
    }
  }

  const next = [...sections];
  next.splice(insertAt, 0, { component: "learnerTestimonials" } as T);
  return next;
}
